import React, { useMemo } from 'react'

/**
 * Smart assignee picker for the New Task modal.
 *
 * Replaces the old <PairSelector> + <AssigneeSelector> two-step.
 * Emits a single coherent `(pairId, assigneeId)` value via `onChange`
 * so the modal never has to reconcile the two fields.
 *
 * Behaviour by pair-set shape:
 *   - 0 pairs        → friendly empty message; never emits.
 *   - 1 pair, viewer = report → primary options [Me, manager-name].
 *   - 1 pair, viewer = manager → primary options [Me, report-name].
 *   - 2+ pairs that all share the viewer as the same role
 *     (a manager with N reports) → primary options
 *     [Me, report-1-name, report-2-name, …]. Picking a report sets
 *     `(pairId, assigneeId) = (thatReportPair, thatReportId)`. Picking
 *     "Me" reveals a secondary picker labelled "for whose 1:1?" with
 *     the reports; picking sets `(pairId, assigneeId) = (thatPair,
 *     currentUserId)`.
 *   - Mixed (the viewer is both somebody's manager AND has a manager)
 *     → primary options merge: [Me, my-manager, report-1, report-2, …].
 *     Picking a non-Me option fixes both fields. Picking "Me" reveals
 *     the secondary "for which 1:1?" picker listing every pair the
 *     viewer is in.
 *
 * Props:
 *   pairs           — output of usePairs(me, reports)
 *   currentUserId   — viewer
 *   value           — { pairId, assigneeId } | null
 *   onChange        — (next | null) => void
 */
export function CreateTaskAssigneeSelector({
  pairs,
  currentUserId,
  value,
  onChange,
}) {
  // Build the primary option list. Each option is one of:
  //   { kind: 'me' }            — assign to viewer (may need secondary picker)
  //   { kind: 'pair', pairId, assigneeId, label }   — direct (pair, person)
  const options = useMemo(() => {
    if (!pairs || pairs.length === 0) return []
    const out = [{ kind: 'me', value: '__me__', label: 'Me' }]
    // For each pair, surface the OTHER party as a direct option.
    for (const p of pairs) {
      const otherId = p.reportId === currentUserId ? p.managerId : p.reportId
      const otherName = p.role === 'manager' ? p.reportName : p.managerName
      out.push({
        kind: 'pair',
        value: `${p.pairId}::${otherId}`,
        pairId: p.pairId,
        assigneeId: otherId,
        label: otherName,
      })
    }
    return out
  }, [pairs, currentUserId])

  const isMeSelected = value && value.assigneeId === currentUserId

  // The "Me" secondary picker (for whose 1:1?) — only relevant when the
  // viewer's pair set has more than one pair AND the viewer can be the
  // assignee in any of them. Always show when "Me" is the selected
  // primary value and there are 2+ pairs.
  const showSecondary = isMeSelected && pairs.length > 1

  function selectPrimary(rawValue) {
    if (rawValue === '__me__') {
      // "Me" picked. If there's only one pair, fix it immediately.
      if (pairs.length === 1) {
        onChange({ pairId: pairs[0].pairId, assigneeId: currentUserId })
      } else {
        // Otherwise wait for the secondary picker. We still emit a
        // partial value so the modal knows the user is in "Me" mode,
        // but with a null pairId so submission stays disabled until
        // the secondary picker fires.
        onChange({ pairId: null, assigneeId: currentUserId })
      }
      return
    }
    const opt = options.find((o) => o.value === rawValue)
    if (!opt) return
    onChange({ pairId: opt.pairId, assigneeId: opt.assigneeId })
  }

  function selectSecondaryPair(pairId) {
    onChange({ pairId, assigneeId: currentUserId })
  }

  if (!pairs || pairs.length === 0) {
    return (
      <div role="alert" style={styles.empty}>
        You need to be in a 1:1 pair to create a task. Ask your admin to set this up.
      </div>
    )
  }

  // Compute the primary select's current value:
  //   - 'me' if value.assigneeId is the viewer
  //   - the matching pair option otherwise
  //   - empty when value is null
  let primaryValue = ''
  if (value && value.assigneeId === currentUserId) {
    primaryValue = '__me__'
  } else if (value) {
    primaryValue = `${value.pairId}::${value.assigneeId}`
  }

  return (
    <div style={styles.wrapper}>
      <select
        value={primaryValue}
        onChange={(e) => selectPrimary(e.target.value)}
        aria-label="Assignee"
        aria-required="true"
        style={styles.select}
      >
        {!primaryValue && (
          <option value="" disabled>Select an assignee…</option>
        )}
        {options.map((opt) => (
          <option key={opt.value} value={opt.value}>{opt.label}</option>
        ))}
      </select>
      {showSecondary && (
        <div style={styles.secondaryRow}>
          <span style={styles.secondaryLabel}>For whose 1:1?</span>
          <select
            value={value?.pairId ?? ''}
            onChange={(e) => selectSecondaryPair(e.target.value)}
            aria-label="For whose 1:1"
            aria-required="true"
            style={styles.select}
          >
            {!value?.pairId && (
              <option value="" disabled>Select a 1:1…</option>
            )}
            {pairs.map((p) => {
              const otherName = p.role === 'manager' ? p.reportName : p.managerName
              return (
                <option key={p.pairId} value={p.pairId}>{otherName}</option>
              )
            })}
          </select>
        </div>
      )}
    </div>
  )
}

const styles = {
  wrapper: {
    display: 'flex',
    flexDirection: 'column',
    gap: 8,
  },
  select: {
    width: '100%',
    fontSize: 13,
    padding: '6px 8px',
    border: '1px solid var(--border, #e8e8e6)',
    borderRadius: 6,
    background: 'var(--surface, #fff)',
    color: 'var(--text-primary)',
    boxSizing: 'border-box',
  },
  secondaryRow: {
    display: 'flex',
    flexDirection: 'column',
    gap: 4,
    paddingLeft: 12,
    borderLeft: '2px solid var(--border, #e8e8e6)',
  },
  secondaryLabel: {
    fontSize: 11,
    color: 'var(--text-secondary, #888)',
  },
  empty: {
    fontSize: 12,
    padding: '10px 12px',
    background: '#FCEBEB',
    color: '#A32D2D',
    borderRadius: 6,
    lineHeight: 1.4,
  },
}
